<template>
    <UInputDate v-model="calendarValue" :disabled="disabled" class="w-full" :ui="{ trailing: 'pe-1 gap-0.5' }">
        <template #trailing>
            <UButton
                v-if="model && clearable && !disabled"
                color="neutral"
                variant="link"
                size="sm"
                icon="i-lucide-x"
                :aria-label="$t('date.clear')"
                class="px-0.5"
                @click="model = null"
            />
            <UPopover v-model:open="pickerOpen">
                <UButton
                    color="neutral"
                    variant="link"
                    size="sm"
                    icon="i-lucide-calendar"
                    :aria-label="$t('date.pick')"
                    :disabled="disabled"
                    class="px-0.5"
                />

                <template #content>
                    <UCalendar v-model="calendarValue" :week-starts-on="1" class="p-2" />
                </template>
            </UPopover>
        </template>
    </UInputDate>
</template>

<script lang="ts" setup>
    import { CalendarDate } from '@internationalized/date'
    import { computed, ref } from 'vue'

    import { isDay } from '@/utils/calendar'

    /*
     * A DAY FIELD: the model is the API's own "YYYY-MM-DD" string, or null for "no date".
     *
     * Nuxt UI's date input models a `CalendarDate` (vue-spec libraries.md), so the conversion
     * lives here, at the component boundary, once — every form in the console binds a plain
     * string. Typing the segments and picking from the calendar both work; the × clears the
     * date, which a date field cannot otherwise be asked to do.
     */
    const model = defineModel<string | null>({ default: null })

    withDefaults(defineProps<{ clearable?: boolean, disabled?: boolean }>(), { clearable: true, disabled: false })

    const pickerOpen = ref(false)

    const toCalendar = (value: string | null): CalendarDate | null => {
        if (!isDay(value)) return null

        const [year = 1970, month = 1, day = 1] = value.split('-').map(Number)

        return new CalendarDate(year, month, day)
    }

    const fromCalendar = (value: { year: number, month: number, day: number } | null | undefined): string | null => {
        if (!value) return null

        return `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`
    }

    const calendarValue = computed({
        get: (): CalendarDate | null => toCalendar(model.value),
        set: (value: unknown) => {
            model.value      = fromCalendar(value as { year: number, month: number, day: number } | null)
            pickerOpen.value = false
        },
    })
</script>
